import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Shield,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
} from "lucide-react";
import {
  computeCompositeRisk,
  listRisks,
  setComplianceContext,
} from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type {
  ComplianceRisk,
  CompositeRiskResponse,
} from "../../types/compliance";
import ShipmentCasePicker from "./ShipmentCasePicker";

const CATEGORY_COLORS: Record<string, string> = {
  vat: "bg-blue-100 text-blue-800",
  shipment: "bg-amber-100 text-amber-800",
  ecmr: "bg-orange-100 text-orange-800",
  customs: "bg-purple-100 text-purple-800",
  fraud: "bg-red-100 text-red-800",
  duplicate: "bg-red-100 text-red-800",
  invoice: "bg-indigo-100 text-indigo-800",
  missing_fields: "bg-gray-100 text-gray-800",
  delivery_delay: "bg-yellow-100 text-yellow-800",
  traceability: "bg-cyan-100 text-cyan-800",
};

function getCategoryColor(cat: string): string {
  for (const [key, color] of Object.entries(CATEGORY_COLORS)) {
    if (cat.includes(key)) return color;
  }
  return "bg-gray-100 text-gray-700";
}

export default function RiskCenter() {
  const { credentials } = useComplianceAuth();
  const tenantId = credentials?.tenantId ?? "default";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [risks, setRisks] = useState<ComplianceRisk[]>([]);
  const [composite, setComposite] = useState<CompositeRiskResponse | null>(
    null,
  );
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>("");
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [expandedRisk, setExpandedRisk] = useState<string | null>(null);
  const [computingComposite, setComputingComposite] = useState(false);

  useEffect(() => {
    setComplianceContext(tenantId, credentials?.role ?? "COMPLIANCE_ADMIN");
    void loadRisks();
  }, [tenantId, credentials?.role, selectedCaseId]);

  async function loadRisks() {
    setLoading(true);
    setError(null);
    try {
      const risksData = await listRisks(undefined, selectedCaseId || undefined);
      setRisks(risksData.items);
      if (risksData.items.length > 0 && !selectedWorkflow) {
        const wfId = risksData.items[0].workflow_id;
        setSelectedWorkflow(wfId);
        try {
          const comp = await computeCompositeRisk(wfId);
          setComposite(comp);
        } catch {
          // ignore
        }
      } else if (!selectedCaseId) {
        setSelectedWorkflow("");
        setComposite(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load risks");
    } finally {
      setLoading(false);
    }
  }

  async function handleComposite() {
    if (!selectedWorkflow) return;
    setComputingComposite(true);
    try {
      const comp = await computeCompositeRisk(selectedWorkflow);
      setComposite(comp);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Composite calculation failed",
      );
    } finally {
      setComputingComposite(false);
    }
  }

  const severityColor = (sev: string) =>
    sev === "critical"
      ? "text-red-600"
      : sev === "high"
        ? "text-orange-600"
        : sev === "medium"
          ? "text-amber-600"
          : "text-gray-600";

  const visibleRisks = risks;

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex items-center gap-2 mb-6">
        <AlertTriangle size={24} className="text-amber-600" />
        <h1 className="text-2xl font-bold text-gray-900">Risk Center</h1>
      </div>

      <div className="mb-6">
        <ShipmentCasePicker
          selectedCaseId={selectedCaseId}
          selectedWorkflowId={selectedWorkflow}
          onSelectCase={(shipmentCase, detail) => {
            setSelectedCaseId(shipmentCase.case_id);
            const firstWorkflow = detail.documents[0]?.workflow_id ?? "";
            setSelectedWorkflow(firstWorkflow);
            setComposite(null);
          }}
          onSelectWorkflow={(document) => {
            setSelectedWorkflow(document.workflow_id);
            setComposite(null);
          }}
        />
        {selectedCaseId && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span>
              Showing risks linked to shipment case <span className="font-mono">{selectedCaseId}</span>. Select a document workflow above to focus composite risk and recommendations for that file.
            </span>
            <button
              type="button"
              onClick={() => setSelectedCaseId("")}
              className="font-semibold text-emerald-700 hover:text-emerald-900"
            >
              Clear case filter
            </button>
          </div>
        )}
      </div>

      {/* Composite Risk */}
      {composite && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Shield size={16} className="text-blue-600" />
              Composite Risk Score — Workflow{" "}
              {composite.workflow_id.slice(0, 12)}…
            </h2>
            <span
              className={`text-xs px-3 py-1 rounded-full font-bold uppercase ${severityColor(composite.severity)}`}
            >
              {composite.severity}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4">
            {Object.entries(composite.risk_breakdown).map(([key, val]) => (
              <div key={key} className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 uppercase font-medium">
                  {key}
                </p>
                <p
                  className={`text-lg font-bold ${val >= 0.5 ? "text-red-600" : "text-gray-700"}`}
                >
                  {(val * 100).toFixed(0)}%
                </p>
              </div>
            ))}
          </div>

          <p className="text-sm font-semibold text-gray-800 mb-3">
            Overall:{" "}
            <span className={`text-lg ${severityColor(composite.severity)}`}>
              {(composite.overall_risk_score * 100).toFixed(0)}%
            </span>
          </p>

          {composite.recommendations.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-blue-700 mb-1">
                Recommendations
              </p>
              <ul className="list-disc list-inside text-xs text-blue-800 space-y-0.5">
                {composite.recommendations.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Risk List */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">
              Detected Risks ({visibleRisks.length})
            </h2>
            <button
              type="button"
              onClick={loadRisks}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Refresh
            </button>
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center p-8 text-sm text-gray-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-600" />
                Loading risks...
              </div>
            ) : error ? (
              <div className="m-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : visibleRisks.length === 0 ? (
              <p className="p-6 text-sm text-gray-500 text-center">
                {selectedCaseId ? "No risks are linked to this shipment case." : "No risks detected. All workflows are clean."}
              </p>
            ) : null}
            {!loading && !error && visibleRisks.map((risk) => (
              <div key={risk.risk_id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 min-w-0">
                    <AlertTriangle
                      size={16}
                      className={
                        risk.severity === "high"
                          ? "text-red-500 shrink-0 mt-0.5"
                          : "text-amber-500 shrink-0 mt-0.5"
                      }
                    />
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${getCategoryColor(risk.category)}`}
                        >
                          {risk.category.replace(/_/g, " ")}
                        </span>
                        <span
                          className={`text-xs font-bold uppercase ${severityColor(risk.severity)}`}
                        >
                          {risk.severity}
                        </span>
                        <span className="text-xs text-gray-400">
                          {(risk.score * 100).toFixed(0)}%
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">
                        {risk.summary}
                      </p>
                      <p className="text-xs text-gray-400 mt-1 font-mono">
                        {risk.workflow_id.slice(0, 16)}… ·{" "}
                        {new Date(risk.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedRisk(
                        expandedRisk === risk.risk_id ? null : risk.risk_id,
                      )
                    }
                    className="text-gray-400 hover:text-gray-600 shrink-0"
                  >
                    {expandedRisk === risk.risk_id ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar - risk actions */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <TrendingUp size={14} className="text-blue-600" />
              Composite Risk
            </h3>
            {selectedWorkflow ? (
              <p className="mb-3 break-all rounded-md bg-gray-50 px-3 py-2 font-mono text-[11px] text-gray-500">
                {selectedWorkflow}
              </p>
            ) : (
              <p className="mb-3 text-xs text-gray-500">
                Select a document workflow from a shipment case or risk row to calculate its composite risk.
              </p>
            )}
            <button
              type="button"
              onClick={() => void handleComposite()}
              disabled={computingComposite || !selectedWorkflow}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm py-2 rounded-lg flex items-center justify-center gap-2 transition"
            >
              {computingComposite ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RefreshCw size={14} />
              )}
              Compute Composite
            </button>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-sm p-4">
            <h3 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <Shield size={14} className="text-amber-700" />
              Validation Source
            </h3>
            <p className="text-xs leading-relaxed text-amber-800">
              VAT, invoice, and shipment checks are managed in Workflow Center.
              Risk Center reads those outputs and focuses on risk review, score
              calculation, and remediation guidance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
